import React, { useState, useEffect, useRef } from 'react'
import Modal from '@/components/Modal'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { GripVertical, ArrowUp, ArrowDown, FileText, Plus } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { formatFileSize } from '@/utils/fileSize'
import type { DocumentRecord } from '@/types/catalog'

interface ManageGroupFilesModalProps {
  isOpen: boolean
  onClose: () => void
  documents: DocumentRecord[]
  availableDocuments?: DocumentRecord[]
  onConfirm: (reorderedDocIds: string[]) => Promise<void>
}

export const ManageGroupFilesModal: React.FC<ManageGroupFilesModalProps> = ({
  isOpen,
  onClose,
  documents,
  availableDocuments = [],
  onConfirm,
}) => {
  const { t } = useTranslation()
  const [reorderedDocs, setReorderedDocs] = useState<DocumentRecord[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [addPopoverOpen, setAddPopoverOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen) {
      const sorted = [...documents].sort((a, b) => {
        const aIdx = a.properties.sheet_index ? parseInt(a.properties.sheet_index, 10) : 0
        const bIdx = b.properties.sheet_index ? parseInt(b.properties.sheet_index, 10) : 0
        return aIdx - bIdx
      })
      setReorderedDocs(sorted)
    }
  }, [isOpen, documents])

  const addedDocIds = new Set(reorderedDocs.map(d => d.id))
  const filteredAvailable = availableDocuments.filter(d => !addedDocIds.has(d.id))

  const moveUp = (index: number) => {
    if (index === 0) return
    const newDocs = [...reorderedDocs]
    const temp = newDocs[index]
    newDocs[index] = newDocs[index - 1]
    newDocs[index - 1] = temp
    setReorderedDocs(newDocs)
  }

  const moveDown = (index: number) => {
    if (index === reorderedDocs.length - 1) return
    const newDocs = [...reorderedDocs]
    const temp = newDocs[index]
    newDocs[index] = newDocs[index + 1]
    newDocs[index + 1] = temp
    setReorderedDocs(newDocs)
  }

  const addDoc = (doc: DocumentRecord) => {
    setReorderedDocs(prev => [...prev, doc])
    setAddPopoverOpen(false)
  }

  const handleConfirm = async () => {
    setIsSaving(true)
    try {
      await onConfirm(reorderedDocs.map((doc) => doc.id))
    } finally {
      setIsSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('data_catalog:graph_editor.multi_page_set.manage_files_title')}
      className="max-w-2xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            {t('common:buttons.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={isSaving}>
            {t('data_catalog:graph_editor.multi_page_set.manage_files_confirm')}
          </Button>
        </>
      }
    >
      <div ref={containerRef} className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('data_catalog:graph_editor.multi_page_set.manage_files_description')}
        </p>

        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {reorderedDocs.map((doc, index) => (
            <div
              key={doc.id}
              className="flex items-center gap-3 p-4 border border-gray-200 dark:border-dark-border rounded-lg bg-white dark:bg-dark-card hover:bg-gray-50 dark:hover:bg-dark-muted transition-colors"
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <GripVertical className="h-5 w-5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                <FileText className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate text-gray-900 dark:text-dark-text">
                    {doc.name}
                  </p>
                </div>
                <div
                  className="text-xs font-medium px-2 py-1 rounded text-gray-600 dark:text-gray-400"
                >
                  #{index + 1}
                </div>
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => moveUp(index)}
                  disabled={index === 0 || isSaving}
                  className="h-8 w-8"
                  title={t('data_catalog:graph_editor.multi_page_set.move_up', { defaultValue: 'Move up' })}
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => moveDown(index)}
                  disabled={index === reorderedDocs.length - 1 || isSaving}
                  className="h-8 w-8"
                  title={t('data_catalog:graph_editor.multi_page_set.move_down', { defaultValue: 'Move down' })}
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}

          {/* Add button */}
          {availableDocuments.length > 0 && (
            <Popover open={addPopoverOpen} onOpenChange={setAddPopoverOpen}>
              <PopoverTrigger asChild>
                <button
                  className="w-full flex items-center justify-center gap-2 p-3 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-muted transition-colors"
                >
                  <Plus className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                  <span className="text-sm text-gray-500 dark:text-dark-text-muted">
                    {t('data_catalog:graph_editor.multi_page_set.add_pnid')}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[360px] p-0" align="start" side="bottom" container={containerRef.current}>
                <div className="max-h-60 overflow-y-auto">
                  {filteredAvailable.length === 0 ? (
                    <p className="p-4 text-sm text-center text-gray-500 dark:text-dark-text-muted">
                      {t('data_catalog:graph_editor.multi_page_set.no_pnids_available')}
                    </p>
                  ) : (
                    filteredAvailable.map(doc => (
                      <button
                        key={doc.id}
                        type="button"
                        onClick={() => addDoc(doc)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-100 dark:hover:bg-dark-accent transition-colors text-left border-b last:border-b-0 cursor-pointer"
                      >
                        <FileText className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate text-gray-900 dark:text-dark-text">
                            {doc.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-dark-text-muted">
                            {formatFileSize(doc.file_size)}
                          </p>
                        </div>
                        <Plus className="w-4 h-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                      </button>
                    ))
                  )}
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default ManageGroupFilesModal
